import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { getBrowserLang } from '@jsverse/transloco';
import { select, Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  filter,
  Observable,
  of,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { showSnackbarMessage } from 'src/app/reducers/shared/shared.actions';
import { logoutUser } from 'src/app/reducers/user/user.actions';
import { selectLanguage } from 'src/app/reducers/user/user.selectors';
import { ConfigService } from 'src/app/services/config/config.service';

import { GenericsService } from './../generics/generics.service';
import { UserService } from './../user/user.service';

/**
 * Clone request with language headers
 * Auth tokens are sent via HttpOnly cookies (withCredentials set in GenericsService)
 */
function cloneRequest(
  req: HttpRequest<unknown>,
  lang: string,
): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      'Accept-Language': lang || 'en',
      'ngsw-bypass': 'true',
    },
  });
}

/**
 * HTTP interceptor that handles token refresh on 401 errors and adds language headers
 */
export const authInterceptor: HttpInterceptorFn = (
  req,
  next,
): Observable<HttpEvent<unknown>> => {
  let isRefreshing = false;
  let lang = getBrowserLang() || 'en';
  const refreshTokenSubject = new BehaviorSubject<string | null>(null);

  const configService = inject(ConfigService);
  const genericService = inject(GenericsService);
  const userService = inject(UserService);
  const store = inject(Store);

  // Get current language from store synchronously
  store.pipe(select(selectLanguage), take(1)).subscribe((l) => {
    if (l) lang = l;
  });

  const handleServerError = (error: unknown): void => {
    if (
      error instanceof HttpErrorResponse &&
      [0, 500, 502].includes(error?.status)
    ) {
      store.dispatch(showSnackbarMessage({ errorCode: error?.status }));
    }
  };

  // With HttpOnly cookies, browser sends refresh token automatically
  const doRefreshToken = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
    return genericService
      .post<
        Record<string, never>,
        { access?: string; refresh?: string }
      >(configService.refreshTokenEP, {})
      .pipe(
        switchMap(() => {
          userService.saveAccessToken();
          isRefreshing = false;
          refreshTokenSubject.next('refreshed');
          return next(cloneRequest(req, lang)).pipe(
            catchError((error: unknown) => {
              handleServerError(error);
              return throwError(() => error as HttpErrorResponse);
            }),
          );
        }),
        catchError((error: unknown) => {
          isRefreshing = false;
          if ((error as HttpErrorResponse)?.status === 401) {
            store.dispatch(logoutUser());
            return of(null as unknown as HttpEvent<unknown>);
          }
          return throwError(() => error as HttpErrorResponse);
        }),
      );
  };

  const handle401Error = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
    if (isRefreshing) {
      return refreshTokenSubject.pipe(
        filter((token): token is string => token !== null),
        take(1),
        switchMap(() => next(cloneRequest(req, lang))),
      );
    }
    isRefreshing = true;
    refreshTokenSubject.next(null);
    return doRefreshToken(req, next);
  };

  return next(cloneRequest(req, lang)).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error?.status === 401) {
        // Don't try to refresh if the refresh endpoint itself failed
        if (req.url.endsWith('/refresh/')) {
          handleServerError(error);
          return throwError(() => error);
        }

        // With HttpOnly cookies, we can't check if refresh token exists
        // Just try to refresh - if it fails, we'll logout
        if (userService.isAuthenticated()) {
          return handle401Error(req, next);
        }

        // User was never logged in
        store.dispatch(logoutUser());
        return of(null as unknown as HttpEvent<unknown>);
      }

      handleServerError(error);
      return throwError(() => error as HttpErrorResponse);
    }),
  );
};
